import { useState } from "react";
import { CalendarClock, Eye, ChevronDown, ChevronRight } from "lucide-react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { useToast } from "../ui/ToastContainer";
import { useAuth } from "../../contexts/AuthContext";
import { parseApiError } from "../../utils/errorHandler";
import {
  globalChangesService,
  type ExtendPreset,
  type ExtendedEnrollmentSample,
  type GlobalChangesPreviewResult,
} from "../../services/globalChangesService";
import {
  GlobalChangesCourseFilter,
  type GlobalChangesCourseFilterValue,
} from "./GlobalChangesCourseFilter";
import { GlobalChangesConfirmDialog } from "./GlobalChangesConfirmDialog";

const PRESETS: ReadonlyArray<{
  key: ExtendPreset;
  label: string;
  days: number;
}> = [
  { key: "3m", label: "3 months", days: 90 },
  { key: "6m", label: "6 months", days: 180 },
  { key: "9m", label: "9 months", days: 270 },
  { key: "1y", label: "1 year", days: 365 },
  { key: "2y", label: "2 years", days: 730 },
  { key: "3y", label: "3 years", days: 1095 },
];

const formatDate = (iso: string | null): string => {
  if (!iso) return "(none)";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "(none)";
  return parsed.toLocaleDateString();
};

const todayIso = (): string => new Date().toISOString().split("T")[0] ?? "";

const buildFilterPayload = (filter: GlobalChangesCourseFilterValue) => {
  if (filter.allCourses) return { allCourses: true };
  return { allCourses: false, courseIds: filter.courseIds };
};

export function ExtendEnrollmentsCard() {
  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useToast();

  const canExtend = hasPermission("globalChanges:extend");

  const [filter, setFilter] = useState<GlobalChangesCourseFilterValue>({
    allCourses: true,
    courseIds: [],
  });
  const [preset, setPreset] = useState<ExtendPreset | null>(null);
  const [customDate, setCustomDate] = useState("");

  const [preview, setPreview] =
    useState<GlobalChangesPreviewResult | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [lastSample, setLastSample] = useState<ExtendedEnrollmentSample[] | null>(
    null,
  );
  const [sampleOpen, setSampleOpen] = useState(true);

  const filterEmpty = !filter.allCourses && filter.courseIds.length === 0;
  const durationEmpty = preset === null && customDate === "";
  const filterPayload = buildFilterPayload(filter);

  const handlePreview = async () => {
    if (filterEmpty || durationEmpty) return;
    setIsPreviewing(true);
    setPreview(null);
    try {
      const response = await globalChangesService.preview({
        ...filterPayload,
        operation: "extend",
      });
      setPreview(response);
    } catch (err) {
      const parsed = parseApiError(err);
      showError(parsed.message, "Preview Failed");
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleSelectPreset = (key: ExtendPreset) => {
    setPreset(key);
    setCustomDate("");
    setPreview(null);
  };

  const handleSelectCustomDate = (next: string) => {
    setCustomDate(next);
    if (next !== "") {
      setPreset(null);
    }
    setPreview(null);
  };

  const handleChangeFilter = (next: GlobalChangesCourseFilterValue) => {
    setFilter(next);
    setPreview(null);
  };

  const handleOpenConfirm = () => {
    if (!preview) return;
    setIsConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setIsApplying(true);
    try {
      const params: {
        allCourses?: boolean;
        courseIds?: string[];
        preset?: ExtendPreset;
        customDate?: string;
      } = { ...filterPayload };
      if (preset) {
        params.preset = preset;
      } else if (customDate) {
        params.customDate = customDate;
      } else {
        // Should be unreachable because the Apply button is disabled, but
        // guard so the backend doesn't reject with a 400.
        throw new Error("Either a preset or a custom date is required");
      }
      const response = await globalChangesService.extendEnrollments(params);
      showSuccess(
        `Extended ${response.count} enrollment${response.count === 1 ? "" : "s"}.`,
        "Extension Applied",
      );
      setLastSample(response.sample);
      setPreview(null);
      setPreset(null);
      setCustomDate("");
      setIsConfirmOpen(false);
    } catch (err) {
      const parsed = parseApiError(err);
      showError(parsed.message, "Extension Failed");
      // Keep the dialog open so the admin can retry; close it explicitly
      // after they read the error.
      setIsConfirmOpen(false);
    } finally {
      setIsApplying(false);
    }
  };

  const presetSummary = preset
    ? PRESETS.find((p) => p.key === preset)?.label
    : customDate
      ? `custom end date ${formatDate(customDate)}`
      : null;

  const previewCount = preview?.enrollmentsAffected ?? null;
  const largeNThreshold = 5000;

  return (
    <Card>
      <div className="space-y-5">
        <header className="space-y-1">
          <h3 className="text-lg font-semibold text-gray-900">
            Extend Enrollment End Dates
          </h3>
          <p className="text-sm text-gray-600">
            Increase the expiry date of every enrollment matching the course
            filter below. The new expiry is the later of today and the current
            expiry, plus the duration you choose.
          </p>
        </header>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Course filter
          </label>
          <GlobalChangesCourseFilter
            value={filter}
            onChange={handleChangeFilter}
            disabled={isApplying}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Duration
          </label>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => handleSelectPreset(p.key)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  preset === p.key
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-gray-700 border-gray-300 hover:border-indigo-400"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="text-xs text-gray-500">or</div>
          <Input
            type="date"
            value={customDate}
            onChange={(e) => handleSelectCustomDate(e.target.value)}
            disabled={preset !== null}
            min={todayIso()}
          />
          {presetSummary && (
            <p className="text-xs text-gray-500">
              New expiry will be max(today, current expiry) + {presetSummary}.
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
          <Button
            variant="outline"
            size="md"
            onClick={handlePreview}
            disabled={
              filterEmpty || durationEmpty || isPreviewing || isApplying
            }
            className="gap-2"
          >
            <Eye size={16} />
            {isPreviewing ? "Calculating..." : "Preview Impact"}
          </Button>
          <p className="text-sm text-gray-600">
            {previewCount === null
              ? "Not yet previewed."
              : `Will affect ${previewCount.toLocaleString()} enrollment${previewCount === 1 ? "" : "s"}.`}
          </p>
        </div>

        {canExtend ? (
          <div className="pt-2">
            <Button
              variant="primary"
              onClick={handleOpenConfirm}
              disabled={previewCount === null || isApplying}
              className="gap-2"
            >
              <CalendarClock size={16} />
              Apply Extension
            </Button>
          </div>
        ) : (
          <p className="text-xs text-gray-500 pt-2">
            You lack the <code>globalChanges:extend</code> permission; this
            action is read-only.
          </p>
        )}

        {lastSample && lastSample.length > 0 && (
          <section className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setSampleOpen((prev) => !prev)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-800 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span>
                Last applied (showing first {lastSample.length})
              </span>
              {sampleOpen ? (
                <ChevronDown size={16} className="text-gray-500" />
              ) : (
                <ChevronRight size={16} className="text-gray-500" />
              )}
            </button>
            {sampleOpen && (
              <ul className="divide-y divide-gray-100">
                {lastSample.map((entry) => (
                  <li
                    key={entry.id}
                    className="px-4 py-2 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1"
                  >
                    <span className="font-medium text-gray-900 truncate">
                      {entry.courseTitle || entry.courseId}
                    </span>
                    <span className="text-xs text-gray-600">
                      {formatDate(entry.previousExpiresAt)} →{" "}
                      {formatDate(entry.newExpiresAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <GlobalChangesConfirmDialog
          isOpen={isConfirmOpen}
          onClose={() => {
            if (!isApplying) setIsConfirmOpen(false);
          }}
          onConfirm={handleConfirm}
          title="Apply Extension"
          description={
            <span>
              This will update the <strong>enrollment expiry</strong> on every
              matching enrollment by{" "}
              <strong>{presetSummary ?? "the chosen duration"}</strong>. The
              change is written to the database immediately and cannot be
              undone from the UI.
            </span>
          }
          impactLine={
            previewCount !== null
              ? `This will affect ${previewCount.toLocaleString()} enrollment${previewCount === 1 ? "" : "s"}.`
              : "No preview is available for the current selection."
          }
          isLoading={isApplying}
          confirmLabel="Apply Extension"
          largeNCount={previewCount ?? 0}
          requireTypeForLargeN={largeNThreshold}
        />
      </div>
    </Card>
  );
}

export default ExtendEnrollmentsCard;